
import React, { useEffect } from 'react';

export default function ChangelogUpdatesPrompt({
  updateInfo,
  onLater,
  onApply,
}) {
  if (!updateInfo || !Array.isArray(updateInfo.entries) || updateInfo.entries.length === 0) {
    return null;
  }

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const dialog = document.querySelector('[data-changelog-update-dialog]');
    const focusable = dialog?.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])') || [];
    focusable[0]?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onLater();
        return;
      }
      if (event.key !== 'Tab' || focusable.length < 2) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
    };
  }, [onLater]);

  const { entries, totalUnseen } = updateInfo;
  const hasMoreUnseen = Number.isFinite(totalUnseen) && totalUnseen > entries.length;

  return (
    <div className="changelog-update-overlay" data-changelog-update-dialog role="dialog" aria-modal="true" aria-labelledby="changelog-update-title">
      <div className="changelog-update-card">
        <div className="changelog-update-content">
          <div className="changelog-update-header changelog-update-brand-zone">
            <div className="changelog-update-kicker">RBH</div>
            <h2 id="changelog-update-title" className="changelog-update-heading">
              {updateInfo.isNewerFrontendAvailable ? 'Nieuwe versie beschikbaar' : 'Release-updates'}
            </h2>
            <div className="kleine-tekst changelog-update-subtitle">
              {updateInfo.isNewerFrontendAvailable
                ? `RBH ${updateInfo.toVersion} is gepubliceerd. Laad de nieuwe versie wanneer dit uitkomt.`
                : 'Dit zijn de releasewijzigingen die je nog niet hebt bekeken. De huidige RBH-app is al geladen.'}
            </div>
          </div>

          <div className="changelog-update-summary-row">
            <div className="changelog-update-badge">{totalUnseen} update{totalUnseen === 1 ? '' : 's'}</div>
            <div className="kleine-tekst changelog-update-summary-text">
              {updateInfo.isNewerFrontendAvailable ? 'Bekijk de wijzigingen en laad daarna de nieuwste versie.' : 'Bekijk wat er in deze release is veranderd.'}
            </div>
          </div>

          {updateInfo.updateError ? <p className="changelog-update-error" role="alert">{updateInfo.updateError}</p> : null}

          <div className="changelog-update-list" role="list">
            {entries.map((entry) => {
              const key = `${entry?.version || 'unknown'}-${entry?.date || 'no-date'}`;
              const highlights = Array.isArray(entry?.highlights) ? entry.highlights : [];
              return (
                <section key={key} className="changelog-update-item" role="listitem">
                  <div className="changelog-update-item-topline">
                    <span className="changelog-update-version">v{entry?.version || '-'}</span>
                    {entry?.date ? <span className="changelog-update-date">{entry.date}</span> : null}
                  </div>
                  <h3 className="changelog-update-title">{entry?.title || 'Update'}</h3>
                  {highlights.length > 0 && (
                    <ul className="changelog-update-highlights">
                      {highlights.map((highlight, index) => (
                        <li key={`${key}-highlight-${index}`}>{highlight}</li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>

          {hasMoreUnseen && (
            <div className="kleine-tekst changelog-update-more">
              +{totalUnseen - entries.length} oudere update(s) beschikbaar in de changelog.
            </div>
          )}

          <div className="changelog-update-actions">
            <button className="krinkels-button secondary" onClick={onLater}>
              Later
            </button>
            <button className="krinkels-button changelog-update-primary" onClick={onApply}>
              {updateInfo.isNewerFrontendAvailable ? 'Nu bijwerken' : 'Markeer als gelezen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
